import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import "./index.scss";
import { Actions as ApiCallActions } from "../../redux/apiCall/reducers";
import general from "../../utils/general";
import useI18n from "../../i18n/useI18n";

const Select = ({
  name,
  value,
  onChange,
  placeholder,
  loadingPlaceholder = "Loading...",
  icon,
  options = [],
  fetchUrl = "",
  valueKey = "value",
  labelKey = "label",
  prependOptions = [],
  required = false,
  className = "",
  mapOption,
  fetchQueryParams = {},
  ...props
}) => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchedOptions, setFetchedOptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  const observerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const hasUserScrolledRef = useRef(false);
  console.log(icon);
  const serializedQueryParams = useMemo(() => {
    try {
      return JSON.stringify(fetchQueryParams || {});
    } catch (error) {
      return "{}";
    }
  }, [fetchQueryParams]);

  const normalizedQueryParams = useMemo(() => {
    try {
      return JSON.parse(serializedQueryParams);
    } catch (error) {
      return {};
    }
  }, [serializedQueryParams]);

  const mapOptionRef = useRef(mapOption);
  useEffect(() => {
    mapOptionRef.current = mapOption;
  }, [mapOption]);

  const defaultMapOption = useCallback(
    (item) => {
      const val = item?.[valueKey];
      const label = item?.[labelKey];
      if (val === undefined || val === null || label === undefined || label === null) return null;
      return { value: val.toString(), label: label.toString() };
    },
    [labelKey, valueKey]
  );

  const resolveOption = useCallback(
    (item) => {
      const mapper = typeof mapOptionRef.current === "function" ? mapOptionRef.current : defaultMapOption;
      return mapper(item);
    },
    [defaultMapOption]
  );

  const mergeOptions = (existing, incoming, append) => {
    const combined = append ? [...existing, ...incoming] : incoming;
    const seen = new Set();
    return combined.filter((opt) => {
      if (!opt || opt.value === undefined || opt.value === null) return false;
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  };

  const fetchOptions = useCallback(
    (pageToFetch = 1, append = false, term = "") => {
      if (!fetchUrl) return;

      const normalizedSearch = term?.trim();
      setLoading(true);
      const query = { ...normalizedQueryParams };
      const searchParam = props.searchParamName || 'search';

      if (normalizedSearch) {
        query[searchParam] = normalizedSearch;
      }

      if (normalizedSearch && pageToFetch === 1) {
        delete query.page;
        delete query.limit;
      } else {
        query.page = Number(pageToFetch);
      }

      dispatch(
        ApiCallActions.Get({
          url: fetchUrl,
          query,
          onSuccess: ({ data }) => {
            const responseData = data?.data ?? data ?? {};

            const listCandidate = Array.isArray(responseData)
              ? responseData
              : Array.isArray(responseData?.data)
                ? responseData.data
                : Array.isArray(data)
                  ? data
                  : [];

            const resolved = listCandidate.map(resolveOption).filter(Boolean);

            setFetchedOptions((prev) => mergeOptions(prev, resolved, append));

            const meta = data?.meta || responseData?.meta || {};
            const nextPage = meta.page ?? pageToFetch;
            const totalPages =
              meta.totalPages ??
              meta.total_pages ??
              meta.totalpages ??
              (meta.total && (meta.limit || normalizedQueryParams?.limit)
                ? Math.ceil(meta.total / (meta.limit || normalizedQueryParams?.limit))
                : nextPage);

            setPagination({
              page: nextPage,
              totalPages: totalPages || 1,
            });
            setLoading(false);
          },
          onError: ({ errorMessage }) => {
            if (errorMessage) {
              general.notificationError(errorMessage);
            }
            setFetchedOptions((prev) => (append ? prev : []));
            setPagination({ page: 1, totalPages: 1 });
            setLoading(false);
          },
        })
      );
    },
    [dispatch, fetchUrl, normalizedQueryParams, resolveOption]
  );

  const normalizedSearchTerm = useMemo(
    () => (searchTerm ? searchTerm.trim() : ""),
    [searchTerm]
  );

  const triggerSearchFetch = useCallback(
    (term) => {
      if (!fetchUrl) return;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      const nextTerm = term ? term.trim() : "";
      const delay = nextTerm ? 300 : 0;
      searchTimeoutRef.current = setTimeout(() => {
        setFetchedOptions([]);
        setPagination({ page: 1, totalPages: 1 });
        fetchOptions(1, false, nextTerm);
      }, delay);
    },
    [fetchUrl, fetchOptions]
  );

  useEffect(() => {
    if (!fetchUrl) return;
    triggerSearchFetch(normalizedSearchTerm);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchUrl, triggerSearchFetch, normalizedSearchTerm, serializedQueryParams]);

  const effectiveOptions = useMemo(
    () => [...prependOptions, ...(fetchUrl ? fetchedOptions : options)],
    [fetchUrl, fetchedOptions, options, prependOptions]
  );

  const filteredOptions = useMemo(() => {
    if (fetchUrl) return effectiveOptions;
    if (!searchTerm) return effectiveOptions;
    const lowerTerm = searchTerm.toLocaleLowerCase("tr-TR");
    return effectiveOptions.filter((option) =>
      option?.label?.toString().toLocaleLowerCase("tr-TR").includes(lowerTerm)
    );
  }, [effectiveOptions, fetchUrl, searchTerm]);

  const selectedOption = useMemo(() => {
    if (!value && value !== 0) return null;
    return effectiveOptions.find(
      (option) => option?.value?.toString() === value?.toString()
    );
  }, [effectiveOptions, value]);

  const displayLabel = useMemo(() => {
    if (selectedOption?.label) {
      return selectedOption.label;
    }
    if (fetchUrl && loading && effectiveOptions.length === 0) {
      return loadingPlaceholder;
    }
    return placeholder || "";
  }, [effectiveOptions.length, fetchUrl, loading, loadingPlaceholder, placeholder, selectedOption]);

  const isDisabled =
    props.disabled || (fetchUrl && loading && effectiveOptions.length === 0 && !value);

  const handleDropdownToggle = () => {
    if (isDisabled) return;
    setIsFocused(true);
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleAddonClick = (event) => {
    if (isDisabled) return;
    event.preventDefault();
    handleDropdownToggle();
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setIsFocused(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fireChangeEvent = (newValue) => {
    if (typeof onChange === "function") {
      onChange({
        target: {
          name,
          value: newValue,
          type: "select-one",
        },
      });
    }
  };

  const handleOptionSelect = (optionValue) => {
    fireChangeEvent(optionValue);
    closeDropdown();
  };

  const handleDropdownScroll = () => {
    if (!fetchUrl) return;
    hasUserScrolledRef.current = true;
    if (loading) return;
    if (pagination.page >= pagination.totalPages) return;

    const target = dropdownRef.current;
    if (!target) return;

    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 8;
    if (nearBottom) {
      fetchOptions(pagination.page + 1, true, normalizedSearchTerm);
    }
  };

  useEffect(() => {
    if (!fetchUrl || !isOpen) return;
    const root = dropdownRef.current;
    const target = bottomSentinelRef.current;
    if (!root || !target) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasUserScrolledRef.current) return;
        if (loading) return;
        if (pagination.page >= pagination.totalPages) return;
        fetchOptions(pagination.page + 1, true, normalizedSearchTerm);
      },
      {
        root,
        rootMargin: "0px 0px 80px 0px",
        threshold: 0,
      }
    );

    observerRef.current.observe(target);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    fetchUrl,
    isOpen,
    loading,
    pagination.page,
    pagination.totalPages,
    fetchOptions,
    normalizedSearchTerm,
  ]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const container = dropdownRef.current;
      if (container) {
        container.scrollTop = 0;
      }
    }
    if (isOpen) {
      hasUserScrolledRef.current = false;
    }
  }, [isOpen]);

  const displayClassNames = [
    "select-display",
    !selectedOption ? "placeholder" : "",
    isDisabled ? "disabled" : "",
    icon ? "with-addon" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`input-group custom-select-wrapper${isFocused ? " input-group-focus" : ""} ${className}`}
      ref={wrapperRef}
    >
      <input type="hidden" name={name} value={value || ""} required={required} />
      <div
        className={displayClassNames}
        onClick={handleDropdownToggle}
      >
        <span className="select-display-text">{displayLabel}</span>
        {value && !isDisabled && (
          <span
            className="select-clear-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleOptionSelect(null);
            }}
            title={t('COMMON_ACTIONS_CLEAR') || "Clear"}
          >
            <i className="zmdi zmdi-close"></i>
          </span>
        )}
        <span className="select-display-arrow">
          <i className="zmdi zmdi-caret-down"></i>
        </span>
      </div>
      {icon && (
        <span
          style={{ padding: '0 15px 0 0' }}
          className="input-group-addon custom-select-addon"
          onClick={handleAddonClick}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleAddonClick(event);
            }
          }}
        >
          <i className={icon.startsWith("zmdi zmdi-") ? icon : icon.startsWith("zmdi-") ? `zmdi ${icon}` : icon}></i>
        </span>
      )}
      {isOpen && (
        <div
          className="custom-select-dropdown"
          onScroll={handleDropdownScroll}
          onWheel={handleDropdownScroll}
          onTouchMove={handleDropdownScroll}
          ref={dropdownRef}
        >
          <div className="custom-select-search" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              className="custom-select-search-input"
              placeholder={t('COMMON_SEARCH_PLACEHOLDER')}
              value={searchTerm}
              onChange={(e) => {
                const nextValue = e.target.value;
                setSearchTerm(nextValue);
                if (fetchUrl) {
                  triggerSearchFetch(nextValue);
                }
              }}
              autoFocus
            />
          </div>
          {filteredOptions.length === 0 && !loading && (
            <div className="custom-select-option disabled">
              {placeholder || "No results found"}
            </div>
          )}
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`custom-select-option${option.value?.toString() === value?.toString() ? " selected" : ""}`}
              onClick={() => handleOptionSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
          <div ref={bottomSentinelRef} />
          {loading && (
            <div className="custom-select-option loading">
              {loadingPlaceholder}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;




